import { useEffect, useRef, useState } from 'react';
import { LockKeyhole } from 'lucide-react';

import { t } from '../i18n/t';
import { holdDone, holdProgress } from './hold.ts';

/**
 * Press and hold to open (kid engagement PRD F2): a young child's tap does
 * not get through, a grown-up reading the label does. Pointer and keyboard
 * both; letting go or sliding off starts over.
 */
export function HoldGate({ onOpen }: { onOpen: () => void }) {
  const [pressedAt, setPressedAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const frame = useRef(0);

  useEffect(() => {
    if (pressedAt === null) return;
    const tick = () => {
      const at = performance.now();
      if (holdDone(pressedAt, at)) {
        onOpen();
        return;
      }
      setNow(at);
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [pressedAt, onOpen]);

  const start = () => {
    setNow(performance.now());
    setPressedAt(performance.now());
  };
  const stop = () => setPressedAt(null);
  const progress = Math.round(holdProgress(pressedAt, now) * 100);

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body items-center gap-4 text-center">
        <button
          type="button"
          className="btn btn-outline h-auto min-h-(--hit-min) touch-none gap-3 py-3 select-none"
          onPointerDown={(e) => {
            // Touch captures the pointer to the button, so pointerleave would
            // not fire until the finger lifts and sliding off would not reset.
            e.currentTarget.releasePointerCapture(e.pointerId);
            start();
          }}
          onPointerUp={stop}
          onPointerLeave={stop}
          onPointerCancel={stop}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
              e.preventDefault();
              start();
            }
          }}
          onKeyUp={stop}
          onBlur={stop}
        >
          <span
            className="radial-progress text-primary"
            style={{ '--value': progress, '--size': '2.5rem', '--thickness': '4px' } as React.CSSProperties}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <LockKeyhole aria-hidden className="size-4" />
          </span>
          {t('grown-ups-gate')}
        </button>
        <p className="text-base-content/60 text-sm">{t('grown-ups-gate-hint')}</p>
      </div>
    </div>
  );
}
